import { useMemo, useState } from 'react';
import { DEFAULT_RANGE_KEY, presetsFor, rangeFor } from './dateRange';

/**
 * Range state for a list: the picker's selection, and the last valid range,
 * which is what the list filters by. An unfinished custom range (end before
 * start) keeps the previous range applied and reports `error`.
 */
export const useDateRange = () => {
  const [selection, setSelection] = useState({ key: DEFAULT_RANGE_KEY, from: '', to: '' });
  const [range, setRange] = useState(() => rangeFor(DEFAULT_RANGE_KEY));

  const update = (changes) => {
    const next = { ...selection, ...changes };
    // Starting a custom range from the one in force saves retyping it.
    if (changes.key === 'custom' && selection.key !== 'custom') {
      next.from = range.from || '';
      next.to = range.to || '';
    }
    setSelection(next);
    const candidate = rangeFor(next.key, next);
    if (!candidate.error) setRange(candidate);
  };

  const { error } = rangeFor(selection.key, selection);
  return { selection, update, range, error };
};

/** The expense-date picker: presets, plus From and To for a custom range. */
const DateRangeFilter = ({ selection, onChange, error, idPrefix = 'expenseRange' }) => {
  const presets = useMemo(() => presetsFor(), []);
  return (
    <div className="d-flex flex-wrap gap-2 align-items-center">
      <select
        className="form-select form-select-sm"
        style={{ width: 'auto' }}
        value={selection.key}
        onChange={(e) => onChange({ key: e.target.value })}
        aria-label="Expense dates"
      >
        {presets.map((preset) => (
          <option key={preset.key} value={preset.key}>{preset.label}</option>
        ))}
      </select>
      {selection.key === 'custom' && (
        <>
          <label className="visually-hidden" htmlFor={`${idPrefix}From`}>From</label>
          <input
            id={`${idPrefix}From`}
            type="date"
            className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
            style={{ width: 'auto' }}
            value={selection.from}
            onChange={(e) => onChange({ from: e.target.value })}
          />
          <span className="text-muted small">to</span>
          <label className="visually-hidden" htmlFor={`${idPrefix}To`}>To</label>
          <input
            id={`${idPrefix}To`}
            type="date"
            className={`form-control form-control-sm ${error ? 'is-invalid' : ''}`}
            style={{ width: 'auto' }}
            value={selection.to}
            onChange={(e) => onChange({ to: e.target.value })}
          />
          {error && <span className="text-danger small" role="alert">{error}</span>}
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;
